import React from "react";
import ColorPicker from "./ColorPicker";
import {
  getThemeColorKey,
  getThemeColorValue,
  normalizeThemeMode,
} from "../utils/themeColors";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const BoxWidgetSettings = ({ edit, setEdit }) => {
  const props = edit?.props || {};

  const update = (patch) => {
    setEdit((prev) => ({
      ...prev,
      props: {
        ...(prev.props || {}),
        ...patch,
      },
    }));
  };

  const themeMode = normalizeThemeMode(props.themeMode);
  const isLight = themeMode === "light";

  const themeKey = (key) => getThemeColorKey(key, themeMode);
  const themeValue = (key, fallback) =>
    getThemeColorValue(props, key, themeMode, fallback);

  const backgroundType = props.backgroundType || "gradient";

  const gradientColorsValue = themeValue("gradientColors");
  const gradientColors = Array.isArray(gradientColorsValue)
    ? gradientColorsValue
    : ["rgba(56, 74, 94, 0.95)", "rgba(22, 31, 45, 0.95)"];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>

      <div className="form-item">
        <label className="form-lbl" style={{ color: "var(--header-color)" }}>
          Background Type
        </label>

        <select
          className="add-column"
          value={backgroundType}
          onChange={(e) => update({ backgroundType: e.target.value })}
        >
          <option value="solid">Solid</option>
          <option value="gradient">Linear Gradient</option>
        </select>
      </div>
      {backgroundType === "solid" ? (
        <div className="form-item">
          <label className="form-lbl" style={{ color: "var(--header-color)" }}>
            Background Color ({isLight ? "Light" : "Dark"})
          </label>

          <ColorPicker
            id="boxBackgroundColor"
            color={
              themeValue(
                "backgroundColor",
                isLight ? "rgba(255,255,255,0.9)" : "rgba(18, 28, 40, 0.9)"
              )
            }
            setColor={(c) => update({ [themeKey("backgroundColor")]: c })}
          />
        </div>
      ) : (
        <>

          <div className="form-item">
            <label className="form-lbl" style={{ color: "var(--header-color)" }}>
              Gradient Angle (deg)
            </label>

            <input
              type="number"
              className="add-column"
              value={props.gradientAngle ?? 135}
              onChange={(e) =>
                update({ gradientAngle: Number(e.target.value) })
              }
            />
          </div>
          <div className="form-item">
            <label className="form-lbl" style={{ color: "var(--header-color)" }}>
              Gradient Colors ({isLight ? "Light" : "Dark"})
            </label>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {gradientColors.map((color, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <ColorPicker
                      id={`gradientColor_${idx}`}
                      color={color}
                      setColor={(c) => {
                        const next = [...gradientColors];
                        next[idx] = c;

                        update({ [themeKey("gradientColors")]: next });
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="details-add-btn"
                    style={{
                      width: "44px",
                      padding: "8px 0",
                      background: "var(--header-background)",
                    }}
                    onClick={() => {
                      if (gradientColors.length <= 2) return;

                      const next = gradientColors.filter((_, i) => i !== idx);

                      update({ [themeKey("gradientColors")]: next });
                    }}
                    disabled={gradientColors.length <= 2}
                    title="Remove color"
                  >
                    -
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="details-add-btn"
                style={{ width: "180px" }}
                onClick={() =>
                  update({
                    [themeKey("gradientColors")]: [
                      ...gradientColors,
                      gradientColors[gradientColors.length - 1] || "#ffffff",
                    ],
                  })
                }
              >
                <span>+</span>
                <span>Add Color</span>
              </button>
            </div>
          </div>
        </>
      )}

      <div className="form-item">
        <label className="form-lbl" style={{ color: "var(--header-color)" }}>
          Opacity (0 - 1)
        </label>

        <input
          type="number"
          className="add-column"
          step="0.05"
          min="0"
          max="1"
          value={props.opacity ?? 1}
          onChange={(e) =>
            update({ opacity: clamp(Number(e.target.value), 0, 1) })
          }
        />
      </div>

      <div className="form-item">
        <label className="form-lbl" style={{ color: "var(--header-color)" }}>
          Border Radius (px)
        </label>

        <input
          type="number"
          className="add-column"
          value={props.borderRadius ?? 16}
          onChange={(e) => update({ borderRadius: Number(e.target.value) })}
        />
      </div>

      <div style={{ display: "flex", gap: "12px" }}>
        <div className="form-item" style={{ flex: 1 }}>
          <label className="form-lbl" style={{ color: "var(--header-color)" }}>
            Border Width (px)
          </label>

          <input
            type="number"
            className="add-column"
            value={props.borderWidth ?? 1}
            onChange={(e) =>
              update({ borderWidth: Math.max(0, Number(e.target.value)) })
            }
          />
        </div>

        <div className="form-item" style={{ flex: 1 }}>
          <label className="form-lbl" style={{ color: "var(--header-color)" }}>
            Border Color ({isLight ? "Light" : "Dark"})
          </label>

          <ColorPicker
            id="boxBorderColor"
            color={
              themeValue(
                "borderColor",
                isLight ? "rgba(0,0,0,0.15)" : "rgba(255, 255, 255, 0.08)"
              )
            }
            setColor={(c) => update({ [themeKey("borderColor")]: c })}
          />
        </div>
      </div>

      <div className="form-item" style={{ display: "flex", gap: "8px" }}>
        <label
          className="form-lbl"
          style={{ color: "var(--header-color)", padding: "4px 0px" }}
        >
          Box Shadow
        </label>

        <input
          className="checkbox-input"
          type="checkbox"
          checked={props.shadowEnabled ?? true}
          onChange={(e) => update({ shadowEnabled: e.target.checked })}
        />
      </div>

      {(props.shadowEnabled ?? true) ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          {[
            { key: "shadowOffsetX", label: "Offset X (px)" },
            { key: "shadowOffsetY", label: "Offset Y (px)" },
            { key: "shadowBlur", label: "Blur (px)" },
            { key: "shadowSpread", label: "Spread (px)" },
          ].map((f) => (
            <div className="form-item" key={f.key}>
              <label
                className="form-lbl"
                style={{ color: "var(--header-color)" }}
              >
                {f.label}
              </label>

              <input
                type="number"
                className="add-column"
                value={props[f.key] ?? 0}
                onChange={(e) =>
                  update({ [f.key]: Number(e.target.value) })
                }
              />
            </div>
          ))}

          <div className="form-item" style={{ gridColumn: "1 / -1" }}>
            <label className="form-lbl" style={{ color: "var(--header-color)" }}>
              Shadow Color ({isLight ? "Light" : "Dark"})
            </label>

            <ColorPicker
              id="boxShadowColor"
              color={
                themeValue(
                  "shadowColor",
                  isLight ? "rgba(0,0,0,0.15)" : "rgba(0, 0, 0, 0.35)"
                )
              }
              setColor={(c) => update({ [themeKey("shadowColor")]: c })}
            />
          </div>
        </div>
      ) : null}

      <div className="form-item">
        <label className="form-lbl" style={{ color: "var(--header-color)" }}>
          Backdrop Blur (px)
        </label>

        <input
          type="number"
          className="add-column"
          value={props.backdropBlur ?? 0}
          onChange={(e) =>
            update({ backdropBlur: Math.max(0, Number(e.target.value)) })
          }
        />
      </div>

      <p
        style={{
          color: "var(--header-color)",
          margin: 0,
          fontSize: 12,
          opacity: 0.9,
        }}
      >
        Box Widget behaves like Background Image: it can overlap other widgets
        and stays behind them.
      </p>
    </div>
  );
};

export default BoxWidgetSettings;
